import type { Client, ClientStatus, ClientType, AdviserLink, Address, AddressType, AdditionalInsured } from '../../types';
import type { ClientResponseDto, AdditionalInsuredResponseDto } from '../../api/config';
import { AddressResponseDto } from '../../api/config';

function mapPreferredPhone(apiValue: string | null | undefined): 'Mobile' | 'Home' | 'Work' | undefined {
  if (!apiValue) return undefined;
  const map: Record<string, 'Mobile' | 'Home' | 'Work'> = {
    MOBILE: 'Mobile',
    HOME: 'Home',
    WORK: 'Work',
  };
  return map[apiValue] || undefined;
}

function mapGender(apiValue: string | null | undefined): 'Male' | 'Female' | 'Other' {
  if (!apiValue) return 'Other';
  const map: Record<string, 'Male' | 'Female' | 'Other'> = {
    MALE: 'Male',
    FEMALE: 'Female',
    OTHER: 'Other',
  };
  return map[apiValue] || 'Other';
}

function mapRelationship(apiValue: string | null | undefined): AdditionalInsured['relationship'] {
  if (!apiValue) return undefined;
  const map: Record<string, AdditionalInsured['relationship']> = {
    PARTNER_SPOUSE: 'Partner/Spouse',
    PARENT: 'Parent',
    CHILD: 'Child',
    FRIEND: 'Friend',
    OTHER: 'Other',
    POLICY_OWNER: 'Policy Owner',
    BUSINESS_PARTNER: 'Business Partner',
    SIBLING: 'Sibling',
  };
  return map[apiValue] || undefined;
}

function mapAddressType(apiValue: string | null | undefined): AddressType {
  const map: Record<AddressResponseDto.addressType, AddressType> = {
    [AddressResponseDto.addressType.HOME_ADDRESS]: 'Home Address',
    [AddressResponseDto.addressType.BILLING_ADDRESS]: 'Billing Address',
    [AddressResponseDto.addressType.BUSINESS_ADDRESS]: 'Business Address',
    [AddressResponseDto.addressType.CORRESPONDENCE_ADDRESS]: 'Correspondence Address',
    [AddressResponseDto.addressType.BRANCH_ADDRESS]: 'Branch Address',
    [AddressResponseDto.addressType.REGISTERED_ADDRESS]: 'Registered Address',
    [AddressResponseDto.addressType.SITE_ADDRESS]: 'Site Address',
    [AddressResponseDto.addressType.OTHER]: 'Other',
  };
  return map[apiValue as AddressResponseDto.addressType] || 'Home Address';
}

function transformAddresses(apiAddresses: AddressResponseDto[] | null | undefined): Address[] | undefined {
  if (!apiAddresses || !Array.isArray(apiAddresses)) return undefined;

  const transformed = apiAddresses.map((addr): Address => ({
    id: addr.id,
    addressType: mapAddressType(addr.addressType),
    unitStreet: addr.unitStreet || '',
    suburb: addr.suburb || '',
    city: addr.city || '',
    postcode: addr.postcode || '',
    country: addr.country || 'New Zealand',
    lookupSuggestionId: addr.lookupSuggestionId || undefined,
  }));

  // Sort by id to ensure consistent ordering
  return transformed.sort((a, b) => a.id.localeCompare(b.id));
}

function transformAdditionalInsureds(apiInsureds: AdditionalInsuredResponseDto[] | null | undefined): AdditionalInsured[] | undefined {
  if (!apiInsureds || !Array.isArray(apiInsureds)) return undefined;

  const transformed = apiInsureds.map((ai): AdditionalInsured => ({
    id: ai.id,
    title: ai.title ?? undefined,
    firstName: ai.firstName,
    lastName: ai.lastName,
    dob: ai.dob,
    gender: mapGender(ai.gender),
    preferredName: ai.preferredName ?? undefined,
    email: ai.email ?? undefined,
    preferredPhone: mapPreferredPhone(ai.preferredPhone),
    mobile: ai.mobile ?? undefined,
    homePhone: ai.homePhone ?? undefined,
    workPhone: ai.workPhone ?? undefined,
    relationship: mapRelationship(ai.relationship),
  }));

  return transformed.sort((a, b) => a.id.localeCompare(b.id));
}

export function transformClientToFrontend(apiData: ClientResponseDto): Client {
  const clientTypeMap: Record<string, ClientType> = {
    INDIVIDUAL: 'individual',
    BUSINESS: 'business',
  };

  const statusMap: Record<string, ClientStatus> = {
    IN_PROGRESS: 'in-progress',
    BOUND: 'bound',
    LAPSED: 'lapsed',
    RENEWAL_DUE: 'renewal-due',
  };

  const adviserName = apiData.assignedAdviser
    ? `${apiData.assignedAdviser.firstName} ${apiData.assignedAdviser.lastName}`
    : 'Unassigned';

  const getStringValue = (value: any): string | undefined => {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'object') {
      if ('value' in value && typeof (value as any).value === 'string') return (value as any).value;
      if ('text' in value && typeof (value as any).text === 'string') return (value as any).text;
      if ('id' in value && typeof (value as any).id === 'string') return (value as any).id;
      return undefined;
    }
    return undefined;
  };

  // Transform adviserLinks from API response
  const transformAdviserLinks = (links: any[] | null | undefined): AdviserLink[] | undefined => {
    if (!links || !Array.isArray(links)) return undefined;
    
    return links.map((link): AdviserLink => {
      // Map linkType from API enum to frontend type
      const linkTypeMap: Record<string, 'authorised-rep' | 'servicing-adviser'> = {
        AUTHORISED_REP: 'authorised-rep',
        SERVICING_ADVISER: 'servicing-adviser',
      };
      
      // Map commissionRecipient from API enum to frontend type
      const commissionRecipientMap: Record<string, 'adviser' | 'fap' | 'inherit'> = {
        ADVISER: 'adviser',
        FAP: 'fap',
        INHERIT: 'inherit',
      };
      
      // Map status from API enum to frontend type
      const statusMap: Record<string, 'current' | 'historical'> = {
        CURRENT: 'current',
        HISTORICAL: 'historical',
      };
      
      return {
        id: link.id,
        linkType: linkTypeMap[link.linkType] || 'authorised-rep',
        adviserId: link.adviserId,
        adviserUserId: link.adviserUserId || undefined,
        adviserName: link.adviserName || 'Unknown Adviser',
        fapId: link.fapId,
        fapName: link.fapName || 'Unknown FAP',
        effectiveFrom: link.effectiveFrom,
        effectiveTo: link.effectiveTo || undefined,
        commissionRecipient: commissionRecipientMap[link.commissionRecipient] || 'inherit',
        changedBy: link.changedBy || '',
        changedAt: link.changedAt || link.createdAt,
        changedByName: link.changedByName || undefined,
        reason: link.reason || undefined,
        status: statusMap[link.status] || 'historical',
      };
    });
  };

  // Transform addresses from API
  const addresses = transformAddresses(apiData.addresses);

  // Use first address for 'address' field (primary address for display)
  const firstAddress = addresses?.[0];
  const primaryAddress = firstAddress
    ? {
        street: firstAddress.unitStreet,
        suburb: firstAddress.suburb,
        postcode: firstAddress.postcode,
      }
    : {
        street: '',
        suburb: '',
        postcode: '',
      };

  return {
    id: apiData.id,
    title: apiData.title ?? undefined,
    firstName: apiData.firstName,
    lastName: apiData.lastName,
    dob: apiData.dob ?? undefined,
    gender: apiData.gender ? mapGender(apiData.gender) : undefined,
    clientType: clientTypeMap[apiData.clientType] || 'individual',
    status: statusMap[apiData.status] || 'in-progress',
    andoCustomerId: getStringValue(apiData.andoCustomerId),
    andoCustomerCode: apiData.andoCustomerCode ?? undefined,
    andoCustomerSyncError: apiData.andoCustomerSyncError ?? undefined,
    andoCustomerSyncedAt: apiData.andoCustomerSyncedAt ?? undefined,
    address: primaryAddress,
    addresses: addresses,
    phone: apiData.phone,
    email: apiData.email,
    assignedAdviser: apiData.assignedAdviserId,
    assignedAdviserName: adviserName,
    createdAt: apiData.createdAt,
    lastActivity: typeof apiData.lastActivity === 'string' ? apiData.lastActivity : apiData.updatedAt,
    notes: typeof apiData.notes === 'string' ? apiData.notes : undefined,
    isVulnerable: apiData.isVulnerable,
    hasMultiplePolicies: apiData.hasMultiplePolicies,
    unallocatedCash: Number(apiData.unallocatedCash ?? 0),
    manualUnallocatedCash: Number(apiData.manualUnallocatedCash ?? 0),
    totalUnallocatedCash: Number(
      apiData.totalUnallocatedCash ??
        Number(apiData.unallocatedCash ?? 0) + Number(apiData.manualUnallocatedCash ?? 0),
    ),
    companyRegNo: typeof apiData.companyRegNo === 'string' ? apiData.companyRegNo : undefined,
    tradingAs: typeof apiData.tradingAs === 'string' ? apiData.tradingAs : undefined,
    gstNumber: typeof apiData.gstNumber === 'string' ? apiData.gstNumber : undefined,
    mainContact: apiData.mainContact
      ? {
          preferredName: typeof apiData.mainContact.preferredName === 'string' ? apiData.mainContact.preferredName : undefined,
          lastName: typeof apiData.mainContact.lastName === 'string' ? apiData.mainContact.lastName : undefined,
          email: typeof apiData.mainContact.email === 'string' ? apiData.mainContact.email : undefined,
          preferredPhone: mapPreferredPhone(apiData.mainContact.preferredPhone),
          mobile: typeof apiData.mainContact.mobile === 'string' ? apiData.mainContact.mobile : undefined,
          homePhone: typeof apiData.mainContact.homePhone === 'string' ? apiData.mainContact.homePhone : undefined,
          workPhone: typeof apiData.mainContact.workPhone === 'string' ? apiData.mainContact.workPhone : undefined,
          externalReference:
            typeof apiData.mainContact.externalReference === 'string' ? apiData.mainContact.externalReference : undefined,
        }
      : undefined,
    adviserLinks: transformAdviserLinks(apiData.adviserLinks),
    additionalInsureds: transformAdditionalInsureds(apiData.additionalInsureds),
  };
}


